import { gql, useLazyQuery, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import transformArrayToObject from '../../utils/array/transformArrayToObject';

export const GET_SETTINGS = gql`
  query GetSettings($getSettingsArg: GetSettingsArg!) {
    getSettings(getSettingsArg: $getSettingsArg) {
      message
      status
      statusCode
      error
      data {
        settingId
        settingKey
        settingValue
      }
    }
  }
`;

export const useSetting = (keys: string | string[]) => {
  const normalizedKeys = useMemo(
    () => (Array.isArray(keys) ? keys : [keys]),
    [JSON.stringify(keys)],
  );

  const { data, loading, error, refetch } = useQuery(GET_SETTINGS, {
    variables: {
      getSettingsArg: {
        settingsKey: normalizedKeys,
      },
    },
    skip: !normalizedKeys.length,
    fetchPolicy: 'cache-first',
  });

  const settings: Record<string, string> = useMemo(() => {
    const list = data?.getSettings?.data || [];
    if (!list.length) return {};

    const baseMap = transformArrayToObject({
      data: list,
      fieldAsKey: 'settingKey',
      fieldAsValue: 'settingValue',
    });

    // Preserve case-insensitivity so existing consumers are not disturbed
    const map: Record<string, string> = { ...baseMap };
    for (const key of Object.keys(baseMap)) {
      map[key.toLowerCase()] = baseMap[key];
      map[key.toUpperCase()] = baseMap[key];
    }
    return map;
  }, [data]);

  return {
    settings,
    loading,
    error,
    refetch,
  };
};

const useGetSettings = (keys?: string | string[]) => {
  const [getSettingsQuery, { error, loading, data }] =
    useLazyQuery(GET_SETTINGS);

  const normalizedKeys = useMemo(
    () => (Array.isArray(keys) ? keys : keys ? [keys] : []),
    [JSON.stringify(keys)],
  );

  const getSettings = (options?: any) => {
    return getSettingsQuery({
      variables: {
        ...(normalizedKeys.length
          ? {
              getSettingsArg: {
                settingsKey: normalizedKeys,
              },
            }
          : {}),
        ...options?.variables,
      },
      ...options,
    });
  };

  return {
    getSettings,
    error,
    data: data?.getSettings?.data,
    loading,
  };
};

export default useGetSettings;
